import axios from "@core/plugins/axios.plugin"
import { handleServiceCall } from "@services/helpers"
import type {
    ComparatorAnalysis,
    ComparatorOutput,
    ComparatorInputPricerGetData,
    ComparatorInput,
    ComparatorOutputWarning,
} from "@types"

// =====================
// Type definitions des réponses des services
// =====================
/**
 * Réponse de l'API pour les périodes disponibles
 */
export type PeriodAvailableResponse = {
    data: {
        availability: {
            periodes: {
                /**
                 * Date de début de la période considérée au format YYYY-MM-DD
                 */
                date_from: string
                /**
                 * Date de fin de la période considérée au format YYYY-MM-DD
                 */
                date_to: string
                /**
     * Pourcentage de disponibilité entre 0 et 100 (%)

     */
                pct_availability: number
            }[]
            best_periode: {
                /**
                 * Date de début de la période considérée au format YYYY-MM-DD
                 */
                date_from: string
                /**
                 * Date de fin de la période considérée au format YYYY-MM-DD
                 */
                date_to: string
            }
        }
    }
}

/**
 * Type pour les données de période avec puissance et consommation
 */
export type DataPeriodeResponse = {
    data: {
        plages: {
            distributeur: {
                energieactive_elecsoutirage_tout: {
                    sum: number
                }
                plage_elecsoutirage_distributeur: {
                    nostat: string
                }
            }[]
            fournisseur: {
                energieactive_elecsoutirage_tout: {
                    sum: number
                }
                plage_elecsoutirage_fournisseur: {
                    nostat: string
                }
            }[]
        }
        periode: {
            energieactive_elecsoutirage_tout: {
                sum: number
            }
            oc: {
                puissance_reference: number
                puissance_reference_denivele: number
                puissance_reference_pp1: number | null
            }
        }
    }
}

/**
 * Type pour les informations NAF d'une entreprise
 */
export type NafCodeResponse = {
    siren: string
    naf_code: string
    company_name: string
    activity_description: string
}

export type CreateCompareOffersWithoutAnalysisResponse = {
    data: ComparatorOutput
    warnings: ComparatorOutputWarning[]
}
export type CreateCompareOffersByAnalysisResponse = ComparatorAnalysis
export type GetCompareOffersByAnalyseIdResponse = ComparatorAnalysis

export type GetAllAnalysesComparatorOffersResponse = ComparatorAnalysis[]

// =====================
// Service
// =====================

/**
 * Le service permettant de gérer les comparaisons d'offres
 */
export class CourtageService {
    /**
     * Récupère les périodes disponibles pour une source
     * @param sourceId - Identifiant de la source
     * @param abortController - Contrôleur d'annulation
     * @returns Les périodes disponibles
     */
    async getPeriodAvailable(
        sourceId: string,
        abortController?: AbortController
    ): Promise<PeriodAvailableResponse> {
        return handleServiceCall<PeriodAvailableResponse>(async () => {
            const { data } = await axios.get(
                `/api/courtage/comparateur/source/${sourceId}/data_availability`,
                {
                    signal: abortController?.signal,
                }
            )
            return data
        })
    }

    /**
     * Récupère les données de consommation et puissance pour une période
     * @param inputs - Paramètres de la requête
     * @param abortController - Contrôleur d'annulation
     * @returns Les données de consommation et puissance
     */
    async getDataPeriode(
        inputs: ComparatorInputPricerGetData,
        abortController?: AbortController
    ): Promise<DataPeriodeResponse> {
        return handleServiceCall<DataPeriodeResponse>(async () => {
            const { data } = await axios.post(
                `/api/courtage/comparateur/data_periode`,
                inputs,
                {
                    signal: abortController?.signal,
                }
            )
            return data
        })
    }

    /**
     * Lance la comparaison des offres pour une source
     * @param sourceId - Identifiant de la source
     * @param params - Paramètres de la comparaison
     * @param abortController - Contrôleur d'annulation
     * @returns L'analyse de comparaison d'offres
     */
    async createCompareOffers(
        params: ComparatorInput,
        abortController?: AbortController
    ): Promise<CreateCompareOffersWithoutAnalysisResponse> {
        return handleServiceCall<CreateCompareOffersWithoutAnalysisResponse>(
            async () => {
                const { data } = await axios.post(
                    `/api/courtage/comparateur/`,
                    params,
                    {
                        signal: abortController?.signal,
                    }
                )
                return data
            }
        )
    }

    // ========================
    // Analyse de comparaison d'offres
    // ========================

    /**
     * Lance la comparaison des offres pour une source avec la sauvegarde de l'analyse
     * @param sourceId - Identifiant de la source
     * @param params - Paramètres de la comparaison
     * @param abortController - Contrôleur d'annulation
     * @returns L'analyse de comparaison d'offres
     */
    async createCompareOffersAnalysis(
        inputs: ComparatorInput,
        params: {
            _id_building: string
            _id_company: string
            _id_user: string
            _id_source: string
            name?: string
        },
        abortController?: AbortController
    ): Promise<CreateCompareOffersByAnalysisResponse> {
        return handleServiceCall<CreateCompareOffersByAnalysisResponse>(
            async () => {
                const {
                    data: { data },
                } = await axios.post(
                    "/api/courtage/comparateur/analyse",
                    {
                        inputs,
                        _id_building: params._id_building,
                        _id_company: params._id_company,
                        _id_user: params._id_user,
                        name: params.name,
                    },
                    {
                        signal: abortController?.signal,
                    }
                )
                return data
            }
        )
    }

    /**
     * Récupère toutes les analyses de comparaison d'offres pour un bâtiment
     * @param _id_building - Identifiant du bâtiment
     * @param abortController - Contrôleur d'annulation
     * @returns Les analyses de comparaison d'offres
     */
    async getAllAnalysesComparatorOffers(
        _id_building: string,
        abortController?: AbortController
    ): Promise<GetAllAnalysesComparatorOffersResponse> {
        return handleServiceCall<GetAllAnalysesComparatorOffersResponse>(
            async () => {
                const {
                    data: { data },
                } = await axios.get(
                    `/api/metier/analyse/building/${_id_building}`,
                    {
                        params: {
                            type: "comparateur",
                        },
                        signal: abortController?.signal,
                    }
                )
                return data
            }
        )
    }

    /**
     * Supprime une analyse de comparaison d'offres
     * @param analysisId - Identifiant de l'analyse
     * @param abortController - Contrôleur d'annulation
     * @returns
     */
    async deleteCompareOffersAnalysis(
        analysisId: string,
        abortController?: AbortController
    ): Promise<void> {
        return handleServiceCall<void>(async () => {
            await axios.delete(`/api/metier/analyse/${analysisId}`, {
                signal: abortController?.signal,
            })
        })
    }

    // ========================
    // Rapports
    // ========================

    /**
     * Récupère le rapport PDF d'une analyse de comparaison d'offres par son identifiant
     * @param analysisId - Identifiant de l'analyse
     * @param abortController - Contrôleur d'annulation
     * @returns L'url du rapport PDF
     */
    async getCompareOffersExportAnalysisById(
        analysisId: string,
        abortController?: AbortController
    ): Promise<string> {
        return handleServiceCall<string>(async () => {
            const { data } = await axios.get(
                `/api/courtage/comparateur/analyse/${analysisId}/rapport`,
                {
                    signal: abortController?.signal,
                }
            )
            return data?.data?.url
        })
    }
}
